<template>
  <div class="generator-new-page app-detail-page" v-if="model">
    <div class="container">
      <!-- 面包屑 -->
      <div class="gen-breadcrumb">
        <router-link :to="homePath">{{ $t('nav.home') }}</router-link>
        <span class="sep">/</span>
        <router-link :to="appsListPath">{{ $t('home.apps.title') }}</router-link>
        <span class="sep">/</span>
        <span>{{ appTitle }}</span>
      </div>

      <!-- 两栏布局 -->
      <div class="gen-layout-new">
        <!-- 左栏：参数 -->
        <div class="gen-form-col">
          <!-- 可滚动区域 -->
          <div class="gen-form-scroll">
            <!-- App 展示图/视频（替换模型选择器） -->
            <div class="app-cover-card" @mouseenter="onCoverEnter" @mouseleave="onCoverLeave">
              <video
                v-if="appVideo"
                :src="appVideo"
                :poster="appImage"
                class="app-cover-video"
                loop
                muted
                playsinline
                ref="coverVideo"
              ></video>
              <img v-else :src="appImage" :alt="appTitle" class="app-cover-img" />
              <div class="app-cover-overlay">
                <div class="app-cover-title">{{ appTitle }}</div>
                <div class="app-cover-model">{{ model.name }} &middot; {{ model.credits }} credits &middot; {{ model.speed }}</div>
              </div>
              <button class="app-cover-switch-btn" @click.stop="showAppsModal = true" :title="$t('home.apps.title')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              </button>
            </div>

            <!-- Prompt 输入 -->
            <div class="gen-prompt-card">
              <textarea
                class="gen-prompt-textarea"
                v-model="paramValues.prompt"
                :placeholder="$t('home.generatorNew.promptPlaceholderImage')"
                rows="4"
                :maxlength="promptMaxlength"
                @keydown.enter.ctrl="generate"
                @keydown.enter.meta="generate"
              ></textarea>
              <div class="gen-prompt-footer">
                <span class="gen-prompt-count">{{ (paramValues.prompt || '').length }}/{{ promptMaxlength }}</span>
              </div>
            </div>

            <!-- 参数面板 -->
            <div class="gen-params-card">
              <ModelParams
                :modelId="mixinModelId"
                :compact="false"
                :values="paramValues"
                :uploadLoading="uploadLoadingMap"
                :uploadFileNames="uploadFileNames"
                @change="onParamChange"
                @upload="onParamUpload"
                @remove-upload="onRemoveUpload"
              />
            </div>
          </div>

          <!-- 积分 + 生成按钮（固定底部） -->
          <div class="gen-actions gen-actions-fixed">
            <div class="gen-credits-info">
              <span class="credits-num">{{ userCredits }}</span> {{ $t('modelDetail.creditsRequired') }}
              <span :class="insufficientCredits ? 'credits-insufficient' : 'credits-required'">{{ requiredCredits }}</span>
            </div>
            <button
              v-if="!insufficientCredits"
              class="gen-btn-generate"
              @click="generate"
              :disabled="generating"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generating ? ($t('home.generatorNew.generating') || 'Generating...') : $t('home.generate') }}</span>
            </button>
            <router-link
              v-else
              class="gen-btn-credits"
              :to="pricingPath"
            >{{ $t('modelDetail.getMoreCredits') }}</router-link>
          </div>

          <!-- 错误消息 -->
          <div class="gen-error-msg" v-if="generateError">{{ generateError }}</div>
        </div>

        <!-- 右栏：历史记录 -->
        <div class="gen-history-col">
          <div class="gen-history-header">
            <span>{{ $t('home.generatorNew.historyTitle') }}</span>
            <router-link class="gen-history-assets-btn" :to="assetsPath">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              <span>{{ $t('home.generatorNew.myAssets') }}</span>
            </router-link>
          </div>
          <GenerationHistory
            :items="historyItems"
            type="image"
            :generating="generating"
          />
        </div>
      </div>
    </div>

    <!-- Apps 弹窗（放在根层级，避免被右栏覆盖） -->
    <div class="apps-modal-overlay" v-show="showAppsModal" @click.self="showAppsModal = false">
      <div class="apps-modal">
        <div class="apps-modal-header">
          <h3>{{ $t('home.apps.title') }}</h3>
          <button class="apps-modal-close" @click="showAppsModal = false">&times;</button>
        </div>
        <div class="apps-modal-body">
          <AppsGrid mode="modal" @select="showAppsModal = false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
var generationMixin = require('../mixins/generationMixin')
var generationHistoryMixin = require('../mixins/generationHistoryMixin')
var ModelParams = require('../components/common/ModelParams.vue').default
var GenerationHistory = require('../components/common/GenerationHistory.vue').default
var AppsGrid = require('../components/AppsGrid.vue').default
var getModelParams = require('../config/modelParams').getModelParams
var appConfig = require('../config/appConfig')

module.exports = {
  name: 'AppDetail',
  mixins: [generationMixin, generationHistoryMixin],
  components: {
    ModelParams: ModelParams,
    GenerationHistory: GenerationHistory,
    AppsGrid: AppsGrid
  },
  data: function () {
    return {
      mixinType: 'image',
      mixinDefaultAiName: 'App Model',
      showAppsModal: false
    }
  },
  computed: {
    appKey: function () {
      return this.$route.params.appId || ''
    },
    appConfig: function () {
      return appConfig.APP_DETAIL_CONFIG[this.appKey] || appConfig.APP_DETAIL_CONFIG['viral-video']
    },
    mixinModelId: function () {
      return this.appConfig ? this.appConfig.model : 'flux-pro-1-1-ultra'
    },
    appTitle: function () {
      /* 路由参数是 kebab-case（viral-video），i18n key 是 camelCase（viralVideo） */
      var camelKey = this.appKey.replace(/-([a-z])/g, function (m, c) { return c.toUpperCase() })
      var items = this.$t('home.apps.items')
      if (items && items[camelKey]) return items[camelKey]
      return this.appKey
    },
    appImage: function () {
      return this.appConfig ? this.appConfig.image : ''
    },
    appVideo: function () {
      return this.appConfig ? this.appConfig.video : null
    },
    model: function () {
      return this.$store.getters.getModelById(this.mixinModelId)
    },
    promptMaxlength: function () {
      var config = getModelParams(this.mixinModelId)
      return (config && config.promptMaxlength) || 2000
    },
    pricingPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/pricing' : '/pricing'
    },
    assetsPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/gallery' : '/gallery'
    },
    homePath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang : '/'
    },
    appsListPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/apps' : '/apps'
    }
  },
  watch: {
    appKey: {
      handler: function () {
        var cfg = appConfig.APP_DETAIL_CONFIG[this.appKey]
        if (cfg) {
          this.mixinType = cfg.type || 'image'
        }
      },
      immediate: true
    },
    mixinModelId: function () {
      this._fetchHistory()
    }
  },
  mounted: function () {
    this._fetchHistory()
    this._applyAppDefaults()
  },
  methods: {
    _applyAppDefaults: function () {
      var cfg = this.appConfig
      if (!cfg) return
      if (cfg.defaultParams && typeof cfg.defaultParams === 'object') {
        var keys = Object.keys(cfg.defaultParams)
        for (var i = 0; i < keys.length; i++) {
          var key = keys[i]
          if (this.paramValues[key] === undefined || this.paramValues[key] === null || this.paramValues[key] === '' || this.paramValues[key] !== cfg.defaultParams[key]) {
            this.$set(this.paramValues, key, cfg.defaultParams[key])
          }
        }
      }
      if (cfg.defaultPrompt && typeof cfg.defaultPrompt === 'string') {
        if (!this.paramValues.prompt || this.paramValues.prompt.trim() === '') {
          this.$set(this.paramValues, 'prompt', cfg.defaultPrompt)
        }
      }
    },
    onCoverEnter: function () {
      var video = this.$refs.coverVideo
      if (video && typeof video.play === 'function') {
        video.play().catch(function () {})
      }
    },
    onCoverLeave: function () {
      var video = this.$refs.coverVideo
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    }
  }
}
</script>

<style scoped>
@import './GeneratorNewStyles.css';

/* App 封面卡片（替换模型选择器） */
.app-cover-card {
  position: relative;
  height: 180px;
}
.app-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 12px;
}
.app-cover-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 12px;
}
.app-cover-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 28px 16px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
  text-align: center;
  border-radius: 0 0 12px 12px;
}
.app-cover-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 2px;
}
.app-cover-model {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}
.app-cover-switch-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  border: none;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
}
.app-cover-switch-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

/* Apps 弹窗 */
.apps-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.apps-modal {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 960px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: apps-modal-in 0.2s ease;
}
@keyframes apps-modal-in {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
.apps-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.apps-modal-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin: 0;
}
.apps-modal-close {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  border-radius: 6px;
}
.apps-modal-close:hover {
  background: #f3f4f6;
  color: #111;
}
.apps-modal-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
}
</style>

<style>
/* 左栏白色主题覆盖全局深色样式 */
.app-detail-page .gen-form-col {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  overflow: hidden !important;
  overflow-y: hidden !important;
}
/* 可滚动区域 */
.app-detail-page .gen-form-scroll {
  flex: 1 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 16px !important;
}
/* 生成按钮固定底部 */
.app-detail-page .gen-actions {
  border-top: none;
}
.app-detail-page .gen-actions-fixed {
  flex-shrink: 0 !important;
  border-top: 1px solid #e5e7eb;
  margin-top: 12px !important;
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 1;
}
/* 参数卡片白色主题 — unscoped 以穿透子组件 */
.gen-params-card .param-item { background: none !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; }
.gen-params-card .btn-group { flex-wrap: wrap; }
.gen-params-card .btn-toggle { padding: 8px 14px; font-size: 13px; }
.gen-params-card .option-group label { color: #333; }
.gen-params-card .switch-title { color: #333; }
.gen-params-card .switch-row { color: #555; }
.gen-params-card .form-group label { color: #333; }
.gen-params-card .optional-label { color: #999; }
.gen-params-card .btn-toggle {
  background: #f7f7f8; border-color: #e0e1e5; color: #555;
}
.gen-params-card .btn-toggle:hover { border-color: #059669; }
.gen-params-card .btn-toggle.active {
  background: #059669; border-color: #059669; color: #fff;
}
.gen-params-card .prompt-input {
  background: #f7f7f8; border-color: #e5e7eb; color: #333;
}
.gen-params-card .prompt-input:focus { border-color: #059669; }
.gen-params-card .seed-input {
  background: #f7f7f8; border: 1px solid #e5e7eb; color: #333; border-radius: 8px; padding: 10px 14px; outline: none;
}
.gen-params-card .seed-input:focus { border-color: #059669; }
.gen-params-card .upload-zone {
  background: #f7f7f8; border-color: #ddd; color: #666;
  border-width: 1.5px; padding: 20px; border-radius: 12px; min-height: 120px;
}
.gen-params-card .upload-zone:hover { border-color: #059669; background: rgba(5, 150, 105, 0.04); }
.gen-params-card .upload-zone .upload-hint { color: #999; }
.gen-params-card .upload-zone .upload-preview { max-height: 160px; border-radius: 8px; }
.gen-params-card .upload-preview-sm { max-width: 140px; max-height: 100px; border-radius: 8px; }
.gen-params-card .upload-zone .upload-preview-label { color: #555; }
.gen-params-card .remove-image-btn { background: rgba(0, 0, 0, 0.5); width: 22px; height: 22px; font-size: 12px; }
.gen-params-card .remove-image-btn:hover { background: rgba(255, 0, 0, 0.6); }
.gen-params-card .switch-slider { background: #d1d5db; }
.gen-params-card input:checked + .switch-slider { background: #059669; }
.gen-params-card .size-btn { background: #f7f7f8; border-color: #e0e1e5; }
.gen-params-card .size-btn:hover { border-color: #059669; }
.gen-params-card .size-btn.active { border-color: #059669; background: #059669; }
.gen-params-card .size-label { color: #666; }
.gen-params-card .size-btn.active .size-label { color: #fff; }
.gen-params-card .gen-type-card { background: #f7f7f8; border-color: #e5e7eb; }
.gen-params-card .gen-type-card:hover { border-color: #059669; }
.gen-params-card .gen-type-card.active { border-color: #059669; background: #059669; }
.gen-params-card .gen-type-card-name { color: #333; }
.gen-params-card .gen-type-card.active .gen-type-card-name { color: #fff; }
.gen-params-card .resolution-chip { background: #f7f7f8; border-color: #e5e7eb; color: #555; }
.gen-params-card .resolution-chip:hover { border-color: #059669; }
.gen-params-card .resolution-chip.active { border-color: #059669; background: #059669; color: #fff; }
.gen-params-card .loading-spinner { border-color: #e0e1e5; border-top-color: #059669; }
</style>
